import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ConstructionIcon from '@mui/icons-material/Construction';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapIcon from '@mui/icons-material/Map';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import './FormsBottomNavbar.css';

const navItems = [
  {
    key: 'home',
    icon: <HomeIcon />,
    route: '/dashboard',
    label: 'Home',
    paths: [
      '/dashboard',
      '/jvjd',
      '/details',
      '/property-search',
      '/property-results',
      '/chat',
      '/jv-jv-property-detail',
      '/User_BankAuction_detail',
      '/postbank_detail',
      '/post-bank-properties',
      '/wanted-prop-details',
      '/all-responses',
      '/pg-hostel-form',
      '/viewlistpage',
      '/matching-buyers',
      '/wishlist-users',
      '/rent-propertymap',
      '/buy-map',
      '/lease_map',
      '/hot-property-map',
      '/new-map',
      '/postbank-card'
    ]
  },
  {
    key: 'construction',
    icon: <ConstructionIcon />,
    route: '/constructions',
    label: 'Construction & Interior',
    paths: [
      '/constructions',
      '/explore-construction-resources',
      '/two-d-plane-interior',
      '/three-d-plane-interior',
      '/elevations',
      '/packages',
      '/interiors',
      '/how-it-works',
      '/get-started',
      '/construction-get-started',
      '/interiorstwod',
      '/interiorsthreed'
    ]
  },

  { key: 'spacer', isSpacer: true },
  {
    key: 'services',
    icon: <CleaningServicesIcon />,
    route: '/home-service',
    label: 'Home Services',
    paths: ['/work-details', '/home-service-type']
  },
  {
    key: 'profile',
    icon: <AccountCircleIcon />,
    route: '/work-detail',
    label: 'More',
    paths: [
      '/user-buy',
      '/user-profile',
      '/edit-profile',
      '/user-edit-buy',
      '/user-lease',
      '/user-edit-lease',
      '/user-rent',
      '/user-edit-rent',
      '/user-subscription',
      '/user-vendors',
      '/user-edit-vendor',
      '/user-wanted',
      '/user-bankauction',
      '/builtSuitProperties',
      '/inboxlist',
      '/user-bestdeals',
    ]
  },
];

/* ─────────── JV/JD tabs ─────────── */
const JVJD_LIST_ITEM = {
  key: 'jvjd-list',
  icon: <FormatListBulletedIcon />,
  route: '/jv-jv-property-detail',
  label: 'List',
};

const JVJD_MAP_ITEM = {
  key: 'jvjd-map',
  icon: <MapIcon />,
  route: '/jvjd',
  label: 'Map',
};

/* ─────────── Post Bank Card tabs (now points to AuctionList) ─────────── */
const POSTBANK_LIST_ITEM = {
  key: 'postbank-list',
  icon: <FormatListBulletedIcon />,
  route: '/AuctionList',       // ✅ changed from /Post_Bankcard_indetail
  label: 'List',
};

const POSTBANK_MAP_ITEM = {
  key: 'postbank-map',
  icon: <MapIcon />,
  route: '/postbank-card',
  label: 'Map',
};

const centerItem = {
  key: 'post',
  icon: <AddCircleIcon />,
  route: '/post',
  label: '',
  paths: [
    '/lease-form',
    '/sellyourproperty-form',
    '/rent-form',
    '/postyourbestdeal-form',
    '/vendor-form',
    '/builder-registration-form'
  ],
  isCenter: true
};

const CustomBottomNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);

  // ✅ Page detection
  const isJvjdPage = location.pathname.startsWith('/jvjd');
  const isJvPropertyDetailPage = location.pathname.startsWith('/jv-jv-property-detail');

  const isPostBankCardPage = location.pathname.startsWith('/postbank-card');
  const isPostBankIndetailPage = location.pathname.startsWith('/Post_Bankcard_indetail');

  // ✅ Swap Construction tab based on current page
  const activeNavItems = (() => {
    if (isJvjdPage) {
      return navItems.map(item =>
        item.key === 'construction' ? JVJD_LIST_ITEM : item
      );
    }
    if (isJvPropertyDetailPage) {
      return navItems.map(item =>
        item.key === 'construction' ? JVJD_MAP_ITEM : item
      );
    }
    if (isPostBankCardPage) {
      return navItems.map(item =>
        item.key === 'construction' ? POSTBANK_LIST_ITEM : item
      );
    }
    if (isPostBankIndetailPage) {
      return navItems.map(item =>
        item.key === 'construction' ? POSTBANK_MAP_ITEM : item
      );
    }
    return navItems;
  })();

  const isActive = (item, currentPath) => {
    if (item.paths?.some(path => currentPath === path)) return true;
    if (item.paths?.some(path => currentPath.startsWith(path + '/'))) return true;
    if (currentPath.startsWith(item.route + '/')) return true;
    return currentPath === item.route;
  };

  const currentKey = (() => {
    const allItems = [...activeNavItems, centerItem];
    const found = allItems.find(item => isActive(item, location.pathname));
    return found ? found.key : '';
  })();

  const shouldShowIndicator = !isActive(centerItem, location.pathname);

  useEffect(() => {
    const indicator = document.querySelector('.cbn-indicator');
    if (!indicator) return;

    if (!shouldShowIndicator) {
      indicator.style.display = 'none';
      return;
    }

    const current = document.querySelector(`.cbn-nav-item[data-key="${currentKey}"]`);
    if (current) {
      indicator.style.display = 'block';

      let transformOffset = 0;
      if (currentKey === 'construction') {
        transformOffset = -10;
      } else if (currentKey === 'services') {
        transformOffset = 10;
      }

      const { offsetLeft, offsetWidth } = current;
      indicator.style.left = `${offsetLeft + offsetWidth / 2 - 16 + transformOffset}px`;
    }
  }, [currentKey, location.pathname, shouldShowIndicator]);

  const handleNav = (item) => {
    if (item.isSpacer) return;

    /* ─────────── JV/JD ─────────── */
    if (item.key === 'jvjd-list') {
      navigate('/jvjd-list');
      return;
    }
    if (item.key === 'jvjd-map') {
      navigate('/jvjd');
      return;
    }

    /* ─────────── Post Bank Card ─────────── */
    if (item.key === 'postbank-list') {
      // ✅ Now navigates to AuctionList
      navigate('/AuctionList');
      return;
    }
    if (item.key === 'postbank-map') {
      navigate('/postbank-card');
      return;
    }

    navigate(item.route);
  };

  return (
    <div className="cbn-wrapper">
      <div className="cbn-bar" ref={navRef}>
        {activeNavItems.map((item) =>
          item.isSpacer ? (
            <div key="spacer" className="cbn-spacer" />
          ) : (
            <div
              key={item.key}
              data-key={item.key}
              className={`cbn-nav-item ${currentKey === item.key ? 'cbn-active' : ''}`}
              onClick={() => handleNav(item)}
            >
              {item.icon}
              <div className="cbn-label">{item.label}</div>
            </div>
          )
        )}

        <div
          className={`cbn-nav-item cbn-center ${currentKey === centerItem.key ? 'cbn-active' : ''}`}
          data-key={centerItem.key}
          onClick={() => handleNav(centerItem)}
        >
          {centerItem.icon}
          <div className="cbn-label">{centerItem.label}</div>
        </div>

        {shouldShowIndicator && <div className="cbn-indicator" />}
      </div>
    </div>
  );
};

export default CustomBottomNavbar;